import { flow } from 'fp-ts/function';
import { get, getOr, isEmpty, join, size } from 'lodash/fp';
import { createSelector } from 'reselect';
import { ConfigurationFieldFlag, isRequiredConfigurationFieldFlag } from '@mews-distributor/common';
import { BookingEngineTaxMessage, distributorApiBasePath, generalApiBasePath } from '@mews-distributor/data-access';
import { getOrEmptyObject, getStringProp } from '@mews/fp';
import { parentSelector } from '../../selectors/parentSelector';
import { ApplicationState } from '../../types';
import { WithEnterpriseId } from '../../types/selectors/selectorPropTypes';
import { getDaysInterval } from '../global/processConfiguration';
import { getSelectedHotelId } from '../resourceOrder/selectors';
import { ThemeVariant } from '../../components/service/ThemeWrapper';
import {
    AppConfiguration,
    Configuration,
    ConfigurationSettings,
    PropertyConfiguration,
    PropertyConfigurations,
    PropertyFeatures,
    RateCompareConfiguration,
    ThemeConfiguration,
} from './types';

const getConfiguration = createSelector<ApplicationState, ApplicationState, Configuration>(
    parentSelector,
    get('configuration')
);

const getSettings = flow<[ApplicationState], Configuration, ConfigurationSettings>(getConfiguration, get('settings'));

export const getConfigurationIds = flow<[ApplicationState], ConfigurationSettings, string[]>(
    getSettings,
    get('configurationIds')
);

export const getCancellationOffset = flow(getSettings, ({ cancellationOffset }) => cancellationOffset);

export const getAppConfiguration = flow<[ApplicationState], Configuration, AppConfiguration>(
    getConfiguration,
    get('app')
);

export const getClientName = flow<[ApplicationState], AppConfiguration, string>(getAppConfiguration, get('clientName'));

export const getPlatformEnvironment = flow(getAppConfiguration, get<AppConfiguration, 'environment'>('environment'));

export const getPlatformPrimaryUrl = flow<[ApplicationState], AppConfiguration, string>(
    getAppConfiguration,
    get('platformPrimaryUrl')
);

export const getPlatformApiUrl = flow<[ApplicationState], AppConfiguration, string>(
    getAppConfiguration,
    get('platformApiUrl')
);

const getAssetBaseUrl = flow<[ApplicationState], AppConfiguration, string>(getAppConfiguration, get('assetBaseUrl'));

export const getImageBaseUrl = flow(getAppConfiguration, get<AppConfiguration, 'imageBaseUrl'>('imageBaseUrl'));
export const isStandalone = flow(getAppConfiguration, get<AppConfiguration, 'isStandalone'>('isStandalone'), Boolean);

export const getPropertyConfigurations = flow<[ApplicationState], Configuration, PropertyConfigurations>(
    getConfiguration,
    get('hotels')
);

const getSelectedHotelConfiguration = createSelector<
    ApplicationState,
    string,
    PropertyConfigurations,
    PropertyConfiguration | undefined
>(getSelectedHotelId, getPropertyConfigurations, get);

const getPropertyConfiguration = createSelector<
    ApplicationState,
    WithEnterpriseId,
    string,
    PropertyConfigurations,
    PropertyConfiguration | undefined
>(getStringProp('enterpriseId'), getPropertyConfigurations, get);

const getPropertySettings = (state: ApplicationState, { enterpriseId }: Partial<WithEnterpriseId> | undefined = {}) => {
    if (enterpriseId !== undefined) {
        const configuration = getPropertyConfiguration(state, { enterpriseId });
        return configuration?.settings;
    }
    const configuration = getSelectedHotelConfiguration(state);
    return configuration?.settings;
};

const getEnterpriseConfigurationSettings = flow(getPropertyConfiguration, get('settings'));

export const getEnterpriseConfigurationServiceId = flow(getEnterpriseConfigurationSettings, get('serviceId'));

export const getSelectedHotelConfigurationId = flow(getPropertySettings, get('configurationId'));

export const getHotelIds = flow<[ApplicationState], ConfigurationSettings, string[]>(getSettings, get('hotelIds'));

export const getConfigurationOrHotelIds = createSelector(
    getConfigurationIds,
    getHotelIds,
    (configurationIds, hotelIds) => (isEmpty(configurationIds) ? hotelIds : configurationIds)
);

export const getStartDateOffset = flow<[ApplicationState], ConfigurationSettings, number | null>(
    getSettings,
    get('startDateOffset')
);

const getEndDateOffset = flow<[ApplicationState], ConfigurationSettings, number | null>(
    getSettings,
    get('endDateOffset')
);

export const getConfigurationDaysInterval = createSelector(getStartDateOffset, getEndDateOffset, getDaysInterval);

export const getDefaultAdultCount = flow(getPropertySettings, get('defaultAdultCount'));

export const getDefaultChildCount = flow(getPropertySettings, get('defaultChildCount'));

export const getMaxAdultCount = flow(getPropertySettings, (settings) => settings?.maxAdultCount ?? null);

export const getConfigurationCheckoutStatement = createSelector(
    getPropertySettings,
    getOrEmptyObject('checkoutStatement')
);

export const getRequiredFields = flow(getPropertySettings, (settings) => settings?.requiredFields ?? []);

const getBookerReservationCustomerEmailInput = flow(
    getPropertySettings,
    getOr(ConfigurationFieldFlag.Required, 'bookerReservationCustomerEmailInput')
);

// Fallback to VatOnly
export const getPropertyPriceLabel = flow(
    getPropertySettings,
    (settings) => settings?.priceLabel ?? BookingEngineTaxMessage.Vat
);

export const isBookerReservationCustomerEmailFieldRequired = createSelector(
    getBookerReservationCustomerEmailInput,
    isRequiredConfigurationFieldFlag
);

export const getGoogleTagManagerContainerId = flow<[ApplicationState], ConfigurationSettings, string | null>(
    getSettings,
    get('googleTagManagerContainerId')
);

export const getIntroVideoSrc = flow<[ApplicationState], ConfigurationSettings, string | null>(
    getSettings,
    get('introVideoSrc')
);

export const hasVoucherCodeEnabled = flow<[ApplicationState], ConfigurationSettings, boolean>(
    getSettings,
    get('displayVoucherCode')
);

export const hasIncludeEmailInPurchaseEventEnabled = flow<[ApplicationState], ConfigurationSettings, boolean>(
    getSettings,
    get('includeEmailInPurchaseEvent')
);

export const areAfterMidnightReservationsAllowed = flow<[ApplicationState], ConfigurationSettings, boolean>(
    getSettings,
    get('allowAfterMidnightReservations')
);

const getEnterpriseFeatures = flow(getPropertyConfiguration, get('features'));
export const getEnterpriseChildSelectionEnabled = (state: ApplicationState, props: WithEnterpriseId) =>
    getEnterpriseFeatures(state, props)?.enableChildSelection;

const areEnterpriseFlexibleRatesEnabled = flow(
    getEnterpriseFeatures,
    (enterpriseFeatures) => enterpriseFeatures?.displayFlexibleRates ?? false
);

export const areFlexibleRatesEnabled = (state: ApplicationState) => {
    const enterpriseId = getSelectedHotelId(state);
    return areEnterpriseFlexibleRatesEnabled(state, { enterpriseId });
};

export const getEnterpriseDisplayAvailability = flow(getEnterpriseFeatures, get('displayAvailability'));

const getFeatures = createSelector(getSelectedHotelConfiguration, get<PropertyConfiguration, 'features'>('features'));

export const getEnterprisePaymentCardRequirement = flow(
    getEnterpriseConfigurationSettings,
    get('paymentCardRequirement')
);

export const hasSpecialRequestsFieldFeature = flow<
    [ApplicationState],
    PropertyFeatures | undefined,
    boolean | undefined,
    boolean
>(getFeatures, get('specialRequestsField'), Boolean);

export const getRateCompareFeature = flow<
    [ApplicationState],
    PropertyFeatures | undefined,
    RateCompareConfiguration | undefined
>(getFeatures, get('rateCompare'));

export const hasDisplayAvailabilityFeature = flow<
    [ApplicationState],
    PropertyFeatures | undefined,
    boolean | undefined,
    boolean
>(getFeatures, get('displayAvailability'), Boolean);

const getTheme = flow<[ApplicationState], Configuration, ThemeConfiguration>(getConfiguration, get('theme'));

export const getThemePrimaryColor = flow<[ApplicationState], ThemeConfiguration, string | null>(
    getTheme,
    get('primaryColor')
);

export const getThemeVariant = flow<[ApplicationState], ThemeConfiguration, ThemeVariant>(getTheme, get('variant'));

export const getCookieFirstToken = (state: ApplicationState) => {
    const appConfiguration = getAppConfiguration(state);

    // TODO: Will be fixed properly in https://mews.myjetbrains.com/youtrack/issue/kanban-5020/Make-CookieFirst-work-on-localhost
    if (__LOCALHOST__) {
        return null;
    }

    return appConfiguration?.cookieFirstToken;
};

export const getVersion = (): string => __VERSION__;

export const getContentUrl = createSelector<ApplicationState, string, string, string>(
    getAssetBaseUrl,
    getVersion,
    (assetBaseUrl, version) => `${assetBaseUrl}/${version}`
);

export const getDistributorApiUrl = createSelector<ApplicationState, string, string>(
    getPlatformApiUrl,
    (platformApiUrl) => `${platformApiUrl}${distributorApiBasePath}`
);

export const getGeneralApiUrl = createSelector(
    getPlatformApiUrl,
    (platformApiUrl) => `${platformApiUrl}${generalApiBasePath}`
);

export const getVersionedClientName = createSelector<ApplicationState, string, string, string>(
    getClientName,
    getVersion,
    (clientName, version) => `${clientName} ${version}`
);

export const isMultiHotel = createSelector(getHotelIds, (hotelIds) => size(hotelIds) > 1);

const distributorPath = '/distributor/';
export const getStandaloneDistributorUrl = createSelector(
    getPlatformPrimaryUrl,
    getConfigurationOrHotelIds,
    (platformPrimaryUrl, ids) => `${platformPrimaryUrl}${distributorPath}${join(';', ids)}`
);

export const getAppStandaloneDistributorUrl = createSelector(
    getConfigurationOrHotelIds,
    (ids) => `https://app.mews.com${distributorPath}${join(';', ids)}`
);
